<template>
  <div :id="itemModel.vars.id" :class="['w-100', 'overflow-hidden', { '-full-height': fullHeight }]">
    <label
      v-if="itemModel.vars.label"
      :class="
        'form-label' +
        (itemModel.vars.label_attr && itemModel.vars.label_attr.class ? ' ' + itemModel.vars.label_attr.class : '') +
        (itemModel.vars.required ? ' required' : '')
      "
    >
      {{ index ? index + ". " : "" }}{{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        :aria-label="itemModel.vars.help"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>

    <div v-if="!itemModel.vars.hide" :class="['position-relative', { '-full-height': fullHeight }]">
      <BlockLoader :loading="uploading" size="small" />
      <Editor
        v-model="itemModel.vars.value"
        :class="
          'editor' +
          (inline ? ' inline' : '') +
          (fullHeight ? ' -full-height' : '') +
          (itemModel.vars.attr && itemModel.vars.attr.class ? ' ' + itemModel.vars.attr.class : '')
        "
        :disabled="itemModel.vars.disabled === true"
        :init="init"
        ref="editor"
        licenseKey="gpl"
        @blur="blur"
        @change="valid"
        @focus="focus"
        @keyup="onUserInput"
        @paste="onUserInput"
      />
    </div>
    <div
      v-if="errors && (!itemModel.vars.attr || (itemModel.vars.attr && !itemModel.vars.attr['data-hide-error']))"
      class="invalid-feedback d-block"
    >
      {{ errors }}
    </div>
    <WritingAssistantComponent
      ref="assistant"
      v-if="itemModel.vars.attr && itemModel.vars.attr[aiAssistantDataAttr]"
      :inputValue="itemModel.vars.value"
      :fieldData="itemModel.vars.attr[aiAssistantDataAttr]"
      :isDisabled="itemModel.vars.aiAssistantDisabled === true"
      @disabled="disableAssistant"
    />
  </div>
  <MobileFilePickerComponent
    v-if="isMobileDevice"
    :show="showFilePicker"
    @photo="insertMobileImage"
    @close="closeFilePicker"
  />
</template>

<script>
import "tinymce";
import Editor from "@tinymce/tinymce-vue";
import "tinymce/models/dom/model";
import "tinymce/themes/silver/theme";
import "tinymce/skins/ui/oxide/skin.css";
import "tinymce/icons/default/icons.min";
import "tinymce/plugins/image";
import "tinymce/plugins/media";
import "tinymce/plugins/table";
import "tinymce/plugins/lists";
import "tinymce/plugins/advlist";
import "tinymce/plugins/link";
import "tinymce/plugins/autolink";
import "tinymce/plugins/charmap";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/code";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/preview";
import "tinymce/plugins/anchor";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/help";
import "tinymce/plugins/importcss";
import "tinymce/plugins/directionality";
import "tinymce/plugins/visualchars";
import "tinymce/plugins/codesample";
import "tinymce/plugins/pagebreak";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/emoticons";
import "tinymce/plugins/autosave";
import "tinymce/plugins/save";
import "tinymce/plugins/wordcount";
import "tinymce/plugins/emoticons/js/emojis.min";
import { formValidator } from "@/services/form/form-validator";
import { loginManager } from "@/services/security/login-manager";
import { fileManager } from "@/services/file/file-manager";
import tinymce from "tinymce";
import { mapState } from "pinia";
import { useTriadStore } from "@/store/triad/triad";
import { useTrainingCourseStore } from "@/store/training-course/training-course";
import { useThemeStore } from "@/store/theme/theme";
import { i18n } from "@/i18n";
import { apiConnection } from "@/services/api-connection";
import { pictureManager } from "@/services/utilities/picture-manager";
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { notificationManager } from "@/services/utilities/notification-manager";
import { Capacitor } from "@capacitor/core";
import MobileFilePickerComponent from "@/components/utilities/MobileFilePickerComponent.vue";
import WritingAssistantComponent from "@/components/form/WritingAssistantComponent.vue";
import { writingAssistantConstantes } from "@/constantes/writingAssistant";

export default {
  name: "EditorTypeComponent",
  emits: ["editorAction", "draft", "focus", "update", "uploading"],
  data() {
    return {
      itemModel: this.model,
      apiPath: fileManager.getApiPath() ? fileManager.getApiPath().slice(0, -3) : "",
      userToken: loginManager.token,
      conf: "platform",
      path: null,
      windowManager: null,
      uploading: false,
      showFilePicker: false,
      init: {
        width: "100%",
        height: this.height(),
        theme: "silver",
        inline: this.inline,
        language_url: "/js-vendor/tinymce/langs/" + this.getLanguage() + ".js",
        language: this.getLanguage(),
        menubar: true,
        filemanager_crossdomain: true,
        file_picker_callback: this.myFileBrowser,
        promotion: false,
        plugins: [
          "advlist",
          "autolink",
          "link",
          "image",
          "lists",
          "charmap",
          "preview",
          "anchor",
          "searchreplace",
          "wordcount",
          "visualblocks",
          "visualchars",
          "code",
          "fullscreen",
          "insertdatetime",
          "media",
          "nonbreaking",
          "save",
          "table",
          "directionality",
          "emoticons",
        ],
        content_css: "/assets/editor/editor.css",
        content_style: this.getContentStyle(),
        contextmenu: "link image",
        skin: false,
        toolbar:
          "undo redo | styles fontsize | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media | forecolor backcolor",
        newline_behavior: "linebreak",
        paste_data_images: true,
        images_upload_handler: this.imagesUploadHandler,
        setup: this.setupEditor,
        images_file_types: "jpg,jpeg,svg,webp,png,gif",
      },
    };
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: false,
      default: null,
    },
    language: {
      type: String,
      required: false,
      default: "fr",
    },
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    fullHeight: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    isMobileDevice() {
      return Capacitor.getPlatform() !== "web";
    },

    aiAssistantDataAttr() {
      return writingAssistantConstantes.AI_ASSISTANT_DATA_ATTR;
    },

    ...mapState(useTriadStore, {
      triad: (store) => store.triad,
    }),
    ...mapState(useTrainingCourseStore, {
      trainingCourse: (store) => store.trainingCourse,
    }),
    ...mapState(useThemeStore, {
      locale: (store) => store.locale,
      isDarkMode: (store) => store.is_dark_mode,
    }),

    errors() {
      return this.model.vars.error;
    },

    subject() {
      if (this.conf === "triad" && this.triad && this.triad.cryptedId) {
        return this.triad.cryptedId;
      }
      if (this.conf === "training_course" && this.trainingCourse && this.trainingCourse.cryptedId) {
        return this.trainingCourse.cryptedId;
      }
      return null;
    },
  },
  watch: {
    "itemModel.vars.error"() {
      this.getClass();
    },

    isDarkMode() {
      this.applyEditorTheme();
    },
  },
  created() {
    if (this.isMobileDevice) {
      this.init.menubar = false;
      this.init.toolbar = "mobileImage link | bold italic | alignleft aligncenter alignright alignjustify";
      this.init.contextmenu = "link";
    } else if (this.model.vars.attr && this.model.vars.mce_type) {
      switch (this.model.vars.mce_type) {
        case "simple":
          this.init.menubar = false;
          this.init.toolbar = "undo redo | bold italic | alignleft aligncenter alignright alignjustify";
          this.init.plugins = ["autolink", "link", "lists", "nonbreaking"];
          this.init.height = 150;
          break;
        case "medium":
          this.init.toolbar =
            "undo redo | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link | forecolor backcolor";
          break;
      }
    }

    this.setFileConfig();
  },
  methods: {
    uploadImage(base64Data, originalName) {
      const data = {
        data: base64Data,
        originalName: originalName,
        conf: this.conf,
        path: this.path,
        subject: this.subject,
      };
      this.$emit("uploading");
      return apiConnection.post("/app/file/upload", data);
    },

    showImageSizeDialog(editor, width, height, onConfirm, onCancel) {
      let confirmed = false;
      let isLocked = true;
      let updating = false;
      const aspectRatio = width / height;

      editor.windowManager.open({
        title: i18n.global.t("editor.image_size_title"),
        body: {
          type: "panel",
          items: [
            {
              type: "bar",
              items: [
                {
                  type: "input",
                  name: "width",
                  label: i18n.global.t("editor.image_width"),
                  inputMode: "numeric",
                },
                {
                  type: "button",
                  name: "lock_ratio",
                  text: "",
                  icon: "lock",
                  borderless: true,
                },
                {
                  type: "input",
                  name: "height",
                  label: i18n.global.t("editor.image_height"),
                  inputMode: "numeric",
                },
              ],
            },
            { type: "input", name: "alt", label: i18n.global.t("editor.image_alt") },
          ],
        },
        initialData: {
          width: String(width),
          height: String(height),
          alt: "",
        },
        buttons: [
          { type: "cancel", text: i18n.global.t("cancel") },
          { type: "submit", text: i18n.global.t("valid"), primary: true },
        ],
        onAction(api, details) {
          if (details.name === "lock_ratio") {
            isLocked = !isLocked;
            api.setEnabled("lock_ratio", !isLocked);
          }
        },
        onChange(api, details) {
          if (updating || !isLocked) return;
          const data = api.getData();
          if (details.name === "width") {
            const w = parseInt(data.width);
            if (!isNaN(w) && w > 0) {
              updating = true;
              api.setData({ height: String(Math.round(w / aspectRatio)) });
              updating = false;
            }
          } else if (details.name === "height") {
            const h = parseInt(data.height);
            if (!isNaN(h) && h > 0) {
              updating = true;
              api.setData({ width: String(Math.round(h * aspectRatio)) });
              updating = false;
            }
          }
        },
        onSubmit(api) {
          confirmed = true;
          const data = api.getData();
          onConfirm(parseInt(data.width) || width, parseInt(data.height) || height, data.alt);
          api.close();
        },
        onClose() {
          if (!confirmed) {
            onCancel();
          }
        },
      });
    },

    async imagesUploadHandler(blobInfo, progress) {
      this.uploading = true;
      const tempImg = new Image();
      tempImg.src = URL.createObjectURL(blobInfo.blob());

      try {
        const { width: imgWidth, height: imgHeight } = await new Promise((resolve, reject) => {
          tempImg.onload = () => resolve({ width: tempImg.naturalWidth, height: tempImg.naturalHeight });
          tempImg.onerror = () => reject(new Error("Image load failed"));
        });
        URL.revokeObjectURL(tempImg.src);

        const base64 = await fileManager.blobToBase64(blobInfo.blob());
        let res;
        try {
          res = await this.uploadImage(base64.split(",")[1], blobInfo.filename());
        } catch (error) {
          this.uploading = false;
          throw {
            message: error?.message || i18n.global.t("editor.image_upload_error"),
            remove: true,
          };
        }

        if (!res) {
          this.uploading = false;
          throw { message: "", remove: true };
        }

        progress(100);
        const object = res.responses[0].object;
        const imageUrl = pictureManager.setPicture(object);
        this.uploading = false;

        return await new Promise((resolve, reject) => {
          this.showImageSizeDialog(
            tinymce.activeEditor,
            imgWidth,
            imgHeight,
            (width, height, alt) => {
              resolve(imageUrl);
              setTimeout(() => {
                const editor = tinymce.activeEditor;
                const imgs = editor.dom.select('img[src="' + imageUrl + '"]');
                if (imgs.length) {
                  const img = imgs[imgs.length - 1];
                  editor.dom.setAttrib(img, "width", width);
                  editor.dom.setAttrib(img, "height", height);
                  editor.dom.setAttrib(img, "alt", alt);
                }
              }, 50);
            },
            () => reject({ message: "", remove: true }),
          );
        });
      } catch (err) {
        if (err?.remove) {
          throw err;
        }
        URL.revokeObjectURL(tempImg.src);
        this.uploading = false;
        const message =
          err.message === "Image load failed"
            ? i18n.global.t("editor.image_read_error")
            : i18n.global.t("editor.image_upload_error");
        notificationManager.showNotification("error", message);
        throw { message, remove: true };
      }
    },

    messageCallback(event) {
      if (!event.data.filevalue) return;
      window.tinymceCallBackURL = event.data.filevalue;
      this.windowManager.close();
    },

    setFileConfig() {
      this.conf = this.model.vars.conf_name;
      this.path = this.model.vars.conf_path;
    },

    myFileBrowser(callback, value, meta) {
      window.addEventListener("message", this.messageCallback);
      let cmsURL =
        this.apiPath +
        "manager/?conf=" +
        this.conf +
        "&module=tinymce-api" +
        "&type=" +
        meta.filetype +
        "&bearer=" +
        this.userToken +
        "&extra[path]=" +
        this.path;

      if (this.subject) {
        cmsURL = cmsURL + "&extra[subject]=" + this.subject;
      }

      const windowManagerCSS =
        '<style type="text/css">' +
        ".tox-dialog {max-width: 100%!important; width:97.5%!important; overflow: hidden; height:95%!important; border-radius:0.25em;}" +
        ".tox-dialog__body { padding: 0!important; }" +
        ".tox .tox-form__group{height:100%;}" +
        ".tox-dialog__body-content > div { height: 100%; overflow:hidden}" +
        "</style > ";

      window.tinymceCallBackURL = "";
      this.windowManager = tinymce.activeEditor.windowManager;
      this.windowManager.open({
        title: "File Manager",
        body: {
          type: "panel",
          items: [
            {
              type: "htmlpanel",
              html:
                windowManagerCSS +
                '<iframe src="' +
                cmsURL +
                '"  frameborder="0" style="width:100%; height:100%"></iframe>',
            },
          ],
        },
        buttons: [],
        onClose: () => {
          window.removeEventListener("message", this.messageCallback);
          if (window.tinymceCallBackURL !== "") {
            callback(window.tinymceCallBackURL, {});
          }
          this.windowManager = null;
        },
      });
    },

    height() {
      return this.model.vars.mce_type === "simple" ? 150 : 300;
    },

    blur() {
      this.$emit("draft", this.itemModel);
    },

    onUserInput() {
      this.$refs.assistant?.onUserInput();
    },

    getClass() {
      const content = this.itemModel.vars.value;
      const editor = document.getElementById(this.itemModel.vars.id);
      const tox = this.inline ? editor.querySelector(".inline") : editor.querySelector(".tox-tinymce");
      if (this.itemModel.vars.required && tox) {
        tox.classList.toggle("is-valid", !!content);
        tox.classList.toggle("is-invalid", !content);
      }
    },

    isMobile() {
      return window.innerWidth < 992;
    },

    valid() {
      this.getClass();
      const content = this.itemModel.vars.value;
      if (this.itemModel.vars.required) {
        formValidator.validModel(this.itemModel, this.language);
      }
      this.$emit("update", content);
    },

    focus() {
      this.$emit("focus");
    },

    focusEditor() {
      const editor = this.$refs.editor?.editor;
      if (editor) {
        editor.focus();
      }
    },

    disableAssistant() {
      this.itemModel.vars.aiAssistantDisabled = true;
    },

    applyEditorTheme() {
      if (this.inline || !this.tinyEditor || !this.tinyEditor.getBody()) {
        return;
      }
      this.tinyEditor.getBody().classList.toggle("dark", this.isDarkMode);
    },

    setupEditor(editor) {
      this.tinyEditor = editor;
      editor.on("init", () => this.applyEditorTheme());

      let contentBeforeDialog = null;
      let windowDepth = 0;
      let cleanupTimer = null;

      const countTags = (doc, selector) => doc.querySelectorAll(selector).length;
      const detectInsertedAction = (before, after) => {
        const parser = new DOMParser();
        const beforeDoc = parser.parseFromString(before, "text/html");
        const afterDoc = parser.parseFromString(after, "text/html");

        if (countTags(afterDoc, "img") > countTags(beforeDoc, "img")) {
          return "image";
        }
        if (countTags(afterDoc, "video, audio, iframe, embed") > countTags(beforeDoc, "video, audio, iframe, embed")) {
          return "media";
        }
        if (countTags(afterDoc, "a[href]") > countTags(beforeDoc, "a[href]")) {
          return "link";
        }
        return null;
      };

      const clearPending = () => {
        contentBeforeDialog = null;
        if (cleanupTimer) {
          clearTimeout(cleanupTimer);
          cleanupTimer = null;
        }
      };

      editor.on("ExecCommand", (e) => {
        if (e.command === "InsertUnorderedList") {
          this.$emit("editorAction", { action: "list-bullet" });
        } else if (e.command === "InsertOrderedList") {
          this.$emit("editorAction", { action: "list-numbered" });
        }
      });

      // La boîte de dialogue lien/image/média peut elle-même ouvrir une sous-fenêtre
      // (file_picker_callback custom) : on n'arme la détection qu'au premier niveau
      // (profondeur 0 -> 1), pas sur les fenêtres imbriquées.
      editor.on("OpenWindow", () => {
        if (windowDepth === 0) {
          clearPending();
          contentBeforeDialog = editor.getContent();
        }
        windowDepth++;
      });

      // La fermeture de la boîte (api.close()) est appelée avant que l'insertion
      // réelle (souvent asynchrone, ex: preprocess/upload) ne soit effectuée : on ne
      // peut donc pas comparer le contenu ici. On se contente d'armer un délai de
      // sécurité qui annule la détection si rien n'a changé (dialogue annulé).
      editor.on("CloseWindow", () => {
        windowDepth = Math.max(0, windowDepth - 1);
        if (windowDepth === 0 && contentBeforeDialog !== null) {
          cleanupTimer = setTimeout(clearPending, 3000);
        }
      });

      // L'insertion réelle déclenche toujours un 'change' (immédiat ou différé après
      // la fermeture de la boîte) : c'est le seul signal fiable pour détecter le résultat.
      editor.on("change", () => {
        if (contentBeforeDialog === null) {
          return;
        }
        const currentContent = editor.getContent();
        if (currentContent === contentBeforeDialog) {
          return;
        }
        const action = detectInsertedAction(contentBeforeDialog, currentContent);
        clearPending();
        if (action) {
          this.$emit("editorAction", { action });
        }
      });

      if (this.isMobileDevice) {
        editor.ui.registry.addButton("mobileImage", {
          icon: "image",
          tooltip: i18n.global.t("editor.insert_image"),
          onAction: () => this.openFilePicker(),
        });
      }
    },

    openFilePicker() {
      this.showFilePicker = true;
    },

    async insertMobileImage(photo) {
      this.uploading = true;
      this.showFilePicker = false;

      try {
        const editor = tinymce.activeEditor;

        const tempImg = new Image();
        tempImg.src = "data:image/" + photo.format + ";base64," + photo.base64String;

        const { width: imgWidth, height: imgHeight } = await new Promise((resolve, reject) => {
          tempImg.onload = () => resolve({ width: tempImg.naturalWidth, height: tempImg.naturalHeight });
          tempImg.onerror = reject;
        });

        let res;
        try {
          res = await this.uploadImage(photo.base64String, "image." + photo.format);
        } catch (error) {
          const message = error?.response?.data?.message || i18n.global.t("editor.image_upload_error");
          notificationManager.showNotification("error", message);
          this.uploading = false;
          return;
        }

        if (!res) {
          return;
        }

        const object = res.responses[0].object;
        const imageUrl = pictureManager.setPicture(object);
        this.uploading = false;

        this.showImageSizeDialog(
          editor,
          imgWidth,
          imgHeight,
          (width, height, alt) => {
            const img = editor.dom.create("img", { src: imageUrl, width, height, alt });
            editor.insertContent(img.outerHTML);
          },
          () => {},
        );
      } catch (e) {
        this.uploading = false;
      }
    },

    getContentStyle() {
      return "@import url('https://fonts.googleapis.com/css?family=Nunito:400,700');";
    },

    getLanguage() {
      const localeMap = { fr: "fr-FR", ca: "ca" };
      return localeMap[i18n.global.locale] || "en";
    },

    closeFilePicker() {
      this.showFilePicker = false;
    },
  },
  components: {
    MobileFilePickerComponent,
    Editor,
    BlockLoader,
    WritingAssistantComponent,
  },
};
</script>

<style lang="scss" scoped>
.inline {
  border: 1px solid $m-color_4;
  border-radius: 6px;
  height: 100%;
  padding: 15px;
  line-height: 10px;
  max-height: 96px;
  overflow: scroll;

  &.is-valid {
    border-color: $green;
  }

  &.is-invalid {
    border-color: $red;
  }

  &.-full-height {
    display: block;
    max-height: none;
  }
}

.-full-height {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.position-relative.-full-height {
  flex: 1;
}
</style>
